import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useBasket } from "@/context/BasketContext";
import { useStore } from "@/context/StoreContext";
import { api, formatNaira, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Minus, ShoppingBasket, Send, Loader2, ArrowLeft, CheckCircle2, Bookmark, MessageCircle } from "lucide-react";

export default function Basket() {
  const { items, remove, setQty, clear, total, replace } = useBasket();
  const { settings } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "", note: "", fulfilment: "delivery", company: "" });
  const [sending, setSending] = useState(false);
  const [savingList, setSavingList] = useState(false);
  const [savedInfo, setSavedInfo] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const token = params.get("restore");
    if (token) {
      api.get(`/saved-lists/${token}`)
        .then((r) => { replace(r.data.items || []); toast.success("Your saved list has been restored."); })
        .catch(() => toast.error("Sorry, that saved list could not be found."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const saveForLater = async () => {
    if (items.length === 0) { toast.error("Add some items first."); return; }
    if (!form.email) { toast.error("Enter your email so we can send you the restore link."); return; }
    setSavingList(true);
    try {
      await api.post("/saved-lists", { email: form.email, items, company: form.company });
      setSavedInfo({ count: items.reduce((s, x) => s + x.qty, 0), email: form.email });
      toast.success("Saved! We've emailed you a link to restore this list anytime.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your list. Please try again.");
    } finally {
      setSavingList(false);
    }
  };

  const validateContact = () => {
    if (!form.first_name || !form.last_name || !form.phone) {
      toast.error("Please add your first name, last name and phone number.");
      return false;
    }
    if (form.fulfilment === "delivery" && !form.address.trim()) {
      toast.error("Please add a delivery address, or choose Collection / Pickup.");
      return false;
    }
    return true;
  };

  const sendWhatsApp = () => {
    if (items.length === 0) { toast.error("Your list is empty."); return; }
    if (!validateContact()) return;
    const waNumber = (settings?.whatsapp || "").replace(/[^0-9]/g, "");
    if (!waNumber) { toast.error("WhatsApp isn't set up. Please use email or call us."); return; }
    const fo = form.fulfilment === "collection" ? "Collection / Pickup" : "Delivery";
    const lines = items.map((x) => `\u2022 ${x.name}${x.unit ? ` (${x.unit})` : ""} x${x.qty} \u2014 ${formatNaira(x.price * x.qty)}`).join("\n");
    const text = `Hello BooksNmore, I'd like to order:\n\n${lines}\n\nEstimated total: ${formatNaira(total)}\n\nName: ${form.first_name} ${form.last_name}\nPhone: ${form.phone}\nFulfilment: ${fo}` +
      (form.address ? `\nAddress: ${form.address}` : "") + (form.note ? `\nNote: ${form.note}` : "");
    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    api.post("/orders", {
      customer: { first_name: form.first_name, last_name: form.last_name, phone: form.phone, email: form.email, address: form.address },
      note: form.note, fulfilment: form.fulfilment, channel: "whatsapp", company: form.company,
      items: items.map((x) => ({ name: x.name, unit: x.unit, price: x.price, qty: x.qty, slug: x.slug })),
    }).catch(() => {});
    clear();
    setDone(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone) {
      toast.error("Please add your first name, last name and phone number.");
      return;
    }
    if (!form.email) {
      toast.error("Please add your email address (we'll send you a copy).");
      return;
    }
    if (form.fulfilment === "delivery" && !form.address.trim()) {
      toast.error("Please add a delivery address, or choose Collection / Pickup.");
      return;
    }
    setSending(true);
    try {
      await api.post("/orders", {
        customer: {
          first_name: form.first_name, last_name: form.last_name,
          phone: form.phone, email: form.email, address: form.address,
        },
        note: form.note,
        fulfilment: form.fulfilment,
        company: form.company,
        items: items.map((x) => ({ name: x.name, unit: x.unit, price: x.price, qty: x.qty, slug: x.slug })),
      });
      clear();
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your list. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="basket-success" className="max-w-xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-6" />
        <h1 className="font-display text-4xl font-black text-brand-blue">List sent!</h1>
        <p className="mt-4 text-brand-black/60">Thank you — we've received your shopping list. A member of our team will be in touch within <strong>24 hours</strong> to confirm your order, payment and delivery/pickup.</p>
        <Link to="/shop" className="mt-8 inline-flex items-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 transition-all" data-testid="basket-continue-shopping">
          Continue browsing
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="basket-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-gold mb-6" data-testid="basket-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-brand-blue">My Shopping List</h1>
      <p className="mt-2 text-brand-black/60">Add the items you'd like, fill in your details, and send the list to us — we'll handle the rest.</p>

      {items.length === 0 ? (
        <div className="mt-10 py-20 text-center border-2 border-dashed border-black/20 rounded-lg" data-testid="basket-empty">
          <ShoppingBasket className="w-12 h-12 mx-auto text-brand-blue/40 mb-4" />
          <p className="font-display font-bold text-xl text-brand-blue">Your list is empty</p>
          <Link to="/shop" className="mt-6 inline-block bg-brand-blue text-white font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 transition-all" data-testid="basket-browse">Browse the shop</Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`basket-item-${it.slug}`} className="flex items-center gap-4 bg-white border-2 border-black rounded-lg p-3 shadow-brutal">
                {it.image && <img src={it.image} alt="" className="w-16 h-16 rounded object-cover border-2 border-black" />}
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-brand-black leading-tight">{it.name}</p>
                  {it.unit && <p className="text-xs text-brand-blue/60">{it.unit}</p>}
                  <p className="text-sm font-black text-brand-blue mt-1">{formatNaira(it.price)}</p>
                </div>
                <div className="flex items-center gap-1 border-2 border-black rounded-md">
                  <button onClick={() => setQty(it.id, it.qty - 1)} data-testid={`basket-dec-${it.slug}`} className="p-2 hover:bg-brand-cream"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-8 text-center font-bold text-sm" data-testid={`basket-qty-${it.slug}`}>{it.qty}</span>
                  <button onClick={() => setQty(it.id, it.qty + 1)} data-testid={`basket-inc-${it.slug}`} className="p-2 hover:bg-brand-cream"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => remove(it.id)} data-testid={`basket-remove-${it.slug}`} className="p-2 text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <button onClick={clear} data-testid="basket-clear" className="text-sm font-semibold text-brand-black/50 hover:text-red-600">Clear list</button>
              <p className="font-display font-bold text-brand-blue">Estimated total: <span className="text-brand-black" data-testid="basket-total">{formatNaira(total)}</span></p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="basket-form" className="bg-brand-cream border-2 border-black rounded-lg shadow-brutal-lg p-6 space-y-5 h-fit lg:sticky lg:top-24">
            <h2 className="font-display font-bold text-xl text-brand-blue">Your details</h2>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="basket-first-name" value={form.first_name} onChange={set("first_name")} placeholder="First name *" className="px-3 py-2.5 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
              <input data-testid="basket-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Last name *" className="px-3 py-2.5 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            </div>
            <input data-testid="basket-phone" value={form.phone} onChange={set("phone")} placeholder="Phone number *" className="w-full px-3 py-2.5 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            <input data-testid="basket-email" value={form.email} onChange={set("email")} placeholder="Email * (for your copy)" className="w-full px-3 py-2.5 border-2 border-black rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold" />

            <div>
              <p className="text-sm font-semibold mb-1.5">How would you like to receive your order?</p>
              <div className="grid grid-cols-2 gap-2" data-testid="basket-fulfilment">
                {[["delivery", "Delivery"], ["collection", "Collection / Pickup"]].map(([val, label]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setForm({ ...form, fulfilment: val })}
                    data-testid={`fulfilment-${val}`}
                    className={`px-3 py-2.5 text-sm font-semibold border-2 border-black rounded-md transition-all ${form.fulfilment === val ? "bg-brand-blue text-white shadow-brutal" : "bg-white hover:bg-brand-cream"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <textarea data-testid="basket-address" value={form.address} onChange={set("address")} rows={2} placeholder={form.fulfilment === "delivery" ? "Delivery address *" : "Pickup notes / preferred time (optional)"} className="w-full px-3 py-2.5 border-2 border-black rounded-md bg-white resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            <textarea data-testid="basket-note" value={form.note} onChange={set("note")} rows={2} placeholder="Note (optional)" className="w-full px-3 py-2.5 border-2 border-black rounded-md bg-white resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold" />
            <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

            <div className="pt-4 border-t-2 border-black/10">
              <p className="font-display font-bold text-brand-blue mb-1">Send your list to us</p>
              <p className="text-sm text-brand-black/60 mb-3">We'll receive your list and get in touch to confirm availability, price and payment.</p>
              <button type="button" onClick={sendWhatsApp} data-testid="basket-whatsapp" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3.5 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 active:translate-y-0.5 transition-all">
                <MessageCircle className="w-5 h-5" /> Send order via WhatsApp
              </button>
              <button type="submit" disabled={sending} data-testid="basket-send" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-white text-brand-blue font-semibold px-6 py-3 border-2 border-black rounded-md hover:bg-white/60 transition-all disabled:opacity-60">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send order via email
              </button>
              <p className="mt-3 text-xs text-brand-black/50 text-center">No account needed &middot; We'll reply within 24 hours.</p>
              <div className="mt-4 text-center">
                <button type="button" onClick={saveForLater} disabled={savingList} data-testid="basket-save" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue underline underline-offset-2 hover:text-brand-gold disabled:opacity-60">
                  {savingList ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />} Save list for later
                </button>
              </div>
              {savedInfo && (
                <p className="mt-2 text-sm font-semibold text-green-700 text-center flex items-center justify-center gap-1.5" data-testid="basket-saved-confirm">
                  <CheckCircle2 className="w-4 h-4" /> {savedInfo.count} {savedInfo.count === 1 ? "item" : "items"} saved — link emailed to {savedInfo.email}
                </p>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
